import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Play, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import VisitorNav from '../components/VisitorNav';
import VisitorFooter from '../components/VisitorFooter';
import VisitorTopMenuBar from '../components/VisitorTopMenuBar';
import VisitorMobileBottomNav from '../components/VisitorMobileBottomNav';
import ReaderNav from '../components/ReaderNav';
import ReaderTopMenuBar from '../components/ReaderTopMenuBar';
import ReaderMobileBottomNav from '../components/ReaderMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import RegistrationModal from '../components/RegistrationModal';
import AskAudiaFAQModal from '../components/AskAudiaFAQModal';
import FounderWelcomeModal from '../components/FounderWelcomeModal';
import { Seo, PAGE_SEO } from '../components/Seo';

const FAQPage = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [openItems, setOpenItems] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showAskAudiaModal, setShowAskAudiaModal] = useState(false);
  const [showFounderModal, setShowFounderModal] = useState(false);

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleCategory = (categoryName) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const faqData = [
    {
      category: 'General',
      questions: [
        {
          id: 'general-0',
          question: 'Why does NewAudia™ exist?',
          answer: 'FOUNDER_LINK',
          isFounderLink: true
        },
        {
          id: 'general-1',
          question: 'What is NewAudia™?',
          answer: 'NewAudia™ is a next-generation audiobook platform that transforms manuscripts into interactive NFD-SmartBook™ experiences. We combine AI-generated narration, real-time engagement tracking, and an intelligent AI companion named Audia who can answer questions about any book as you listen. Our platform serves both readers seeking immersive audio experiences and authors/publishers looking for a modern way to distribute their work.'
        },
        {
          id: 'general-2',
          question: 'What is an NFD-SmartBook™?',
          answer: 'An NFD-SmartBook™ (NewAudia Formatted Document SmartBook) is our proprietary audio-first, interactive book format. Unlike traditional audiobooks, NFD-SmartBooks™ feature AI-generated narration, chapter-by-chapter navigation, and integration with Audia—our AI assistant who can answer questions about characters, plot, themes, and more while you listen. It\'s like having a knowledgeable reading companion available 24/7.'
        },
        {
          id: 'general-3',
          question: 'Who is Audia?',
          answer: 'Audia is your AI-powered reading companion. While listening to any NFD-SmartBook™, you can tap "Ask Audia" to ask questions about the story, characters, themes, or even the current chapter you\'re on. Audia responds with both text and voice, making it feel like a natural conversation. She knows the context of where you are in the book and can help you understand complex plots, recall character details, or explore deeper meanings.'
        },
        {
          id: 'general-4',
          question: 'Is NewAudia™ available on mobile?',
          answer: 'Yes! NewAudia™ is fully optimized for mobile browsers on both iOS and Android devices. You can listen, browse your library, and chat with Audia directly from your phone or tablet. Native mobile apps are planned for future release.'
        },
        {
          id: 'general-5',
          question: 'How is NewAudia™ different from Audible or other audiobook platforms?',
          answer: 'Traditional audiobook platforms offer passive listening—you press play and listen. NewAudia™ offers interactive listening. Our AI companion Audia can answer your questions about the book in real-time, helping you understand characters, recall plot points, or explore themes. Additionally, authors can publish instantly using AI-generated narration without the cost and time of hiring voice actors. It\'s audiobooks reimagined for the AI age.'
        },
        {
          id: 'general-6',
          question: 'Is NewAudia™ free to use?',
          answer: 'Your first manuscript upload is free. Additional manuscript uploads are subject to an upload fee per manuscript. No subscription required for authors. Readers receive free Introduction Credits to explore the platform. After that, you can purchase credit bundles or subscribe for continued access. Visit our Pricing page for current details.'
        },
        {
          id: 'general-7',
          question: 'What genres of books are available on NewAudia™?',
          answer: 'NewAudia™ hosts a growing library across all genres—fiction, non-fiction, business, self-help, romance, sci-fi, mystery, and more. As more authors join the platform, our catalog continues to expand. You can browse by genre, author, or theme to find books that match your interests.'
        }
      ]
    },
    {
      category: 'For Readers',
      questions: [
        {
          id: 'readers-1',
          question: 'How do I get started as a reader?',
          answer: 'Sign up for a free Reader account on NewAudia™. New readers receive complimentary Introduction Credits to explore the platform. Browse our library, add books that interest you, and start listening immediately. No downloads required—everything streams directly in your browser.'
        },
        {
          id: 'readers-2',
          question: 'What are Reader Credits and how do they work?',
          answer: 'Reader Credits (RC) power your NewAudia™ experience. Credits are consumed when you listen to non-free books and when you interact with Audia. This gives you complete control over engagement fees based on your personal listening style. New readers receive free Introduction Credits. You can purchase additional credits through bundles on our Pricing page. Subscribers also receive monthly credit allotments and discounted bundle pricing.'
        },
        {
          id: 'readers-3',
          question: 'How do I add books to My Library?',
          answer: 'Browse the public library on the Reader Homepage, find a book you\'re interested in, and click "Add to Library." The book will appear in your personal library where you can start listening, track your progress, and pick up where you left off across sessions.'
        },
        {
          id: 'readers-4',
          question: 'Can I ask Audia questions while listening?',
          answer: 'Absolutely! While listening to any book, tap the "Ask Audia" button to open the chat. You can ask about characters ("Who is Sarah?"), plot points ("What happened in the last chapter?"), themes ("What is the main message of this book?"), or anything else. Audia knows the full context of the book and your current position in it.'
        },
        {
          id: 'readers-5',
          question: 'Will my listening progress be saved?',
          answer: 'Yes! NewAudia™ automatically saves your progress. When you return to a book, you\'ll see a "Welcome Back" prompt offering to continue from where you left off. Your bookmarks, chapter position, and listening history are all preserved across sessions and devices.'
        },
        {
          id: 'readers-6',
          question: 'What subscription options are available?',
          answer: 'We offer flexible options: New readers receive free Introduction Credits. À La Carte allows you to purchase Credit Bundles without a monthly commitment. For regular readers, we offer monthly subscription tiers that provide recurring credits and subscriber perks. Annual subscriptions are also available at a discount. Visit our Pricing page to compare current plans and pricing.'
        },
        {
          id: 'readers-7',
          question: 'Do I have to pay to listen to audiobooks?',
          answer: 'NewAudia™ offers both free and paid content. Promotional and free books can be listened to without consuming credits. For non-free books, Reader Credits are consumed as you listen, giving you control over your engagement costs based on your personal listening style. Credits are also used when interacting with Audia. New readers receive free Introduction Credits to explore the platform.'
        },
        {
          id: 'readers-8',
          question: 'What kind of questions can I ask Audia?',
          answer: 'You can ask Audia almost anything about the book you\'re reading: "Who is the main character?", "What just happened?", "Why did she do that?", "What\'s the relationship between John and Mary?", "Summarize the last chapter", "What are the main themes?", or even "Would I like this book if I enjoyed [another book]?" Audia understands context and provides thoughtful, relevant answers.'
        },
        {
          id: 'readers-9',
          question: 'Can I listen to the same book multiple times?',
          answer: 'Yes! Once a book is in your library, you can listen to it as many times as you like. Your progress is saved, and you can restart from the beginning or jump to any chapter anytime. For free/promotional books, re-listening costs nothing. For non-free books, credits are consumed based on listening engagement.'
        },
        {
          id: 'readers-10',
          question: 'What browsers and devices work with NewAudia™?',
          answer: 'NewAudia™ works on all modern browsers including Chrome, Safari, Firefox, and Edge on both desktop and mobile devices. For the best experience, we recommend using the latest version of your browser. iOS and Android devices are fully supported through mobile browsers.'
        },
        {
          id: 'readers-11',
          question: 'The audio isn\'t playing. What should I do?',
          answer: 'First, check your device volume and ensure your browser isn\'t muted. Try refreshing the page. If issues persist, try a different browser or clear your browser cache. Make sure you have a stable internet connection, as NewAudia™ streams audio in real-time. If problems continue, contact support at info@newaudia.com.'
        },
        {
          id: 'readers-12',
          question: 'Can I get a refund on credits I\'ve purchased?',
          answer: 'Credit purchases are generally non-refundable once applied to your account. However, if you experience technical issues that prevent you from using credits, or if there\'s an error with your purchase, please contact our support team at info@newaudia.com and we\'ll work to resolve the situation.'
        },
        {
          id: 'readers-13',
          question: 'What happens to my credits if I cancel my subscription?',
          answer: 'If you cancel your subscription, any unused credits in your account remain available. Credits don\'t expire, so you can continue using them even after cancellation. You simply won\'t receive new monthly credit allotments or subscriber discounts on bundles.'
        },
        {
          id: 'readers-14',
          question: 'Is there mature or adult content on NewAudia™?',
          answer: 'NewAudia™ hosts books across all genres and content levels. Books are categorized by genre and may include content warnings where applicable. We recommend reviewing book descriptions before adding to your library. Parents should supervise younger readers and review content appropriateness.'
        },
        {
          id: 'readers-15',
          question: 'Can I gift or share credits with someone else?',
          answer: 'Currently, credits are tied to individual accounts and cannot be transferred or gifted directly. However, you can purchase a credit bundle as a gift by creating an account for someone or using our gift card options when available. Check our Pricing page for current gifting options.'
        },
        {
          id: 'readers-16',
          question: 'What happens when I run out of credits?',
          answer: 'When your credit balance reaches zero, you can still browse the library and access free/promotional content. For non-free books, you\'ll be prompted to purchase additional credits before continuing. You can buy credit bundles at any time from the Pricing page. If you\'re a subscriber, your next monthly allotment will replenish your balance on your billing date.'
        }
      ]
    },
    {
      category: 'For Authors & Publishers',
      questions: [
        {
          id: 'creators-1',
          question: 'How do I publish my book on NewAudia™?',
          answer: 'Sign up for an Author or Publisher account. From your console, upload your manuscript in EPUB format. Our system will analyze your content, generate AI narration for each chapter, and create an interactive NFD-SmartBook™. You can customize the chapter outline, preview the audio, and activate your book when ready. The entire process—from upload to publication—can be completed in under an hour for most books.'
        },
        {
          id: 'creators-2',
          question: 'What file formats do you accept?',
          answer: 'We accept EPUB files for manuscript upload. EPUB is the industry standard for digital books and preserves your chapter structure and content. Most writing software (Scrivener, Vellum, Calibre) can export to EPUB.'
        },
        {
          id: 'creators-2b',
          question: 'What is the maximum file size for manuscript uploads?',
          answer: 'The maximum file size for EPUB uploads is 20 MB. Most standard manuscripts fall well within this limit. If your file exceeds 20 MB, consider compressing images within your EPUB or splitting very long works into volumes. Contact support if you need assistance with large manuscripts.'
        },
        {
          id: 'creators-3',
          question: 'How does the AI narration work?',
          answer: 'Our platform uses advanced text-to-speech technology to generate natural-sounding narration for your book. During the upload process, each chapter is converted to audio. You can preview the results before activating your book. The AI voice is designed to be clear, engaging, and suitable for long-form listening.'
        },
        {
          id: 'creators-4',
          question: 'Can I edit the chapter structure after upload?',
          answer: 'Yes! After your manuscript is processed, you\'ll see an Outline Editor where you can review and adjust how your book is divided into segments. You can split long chapters, merge short sections, rename segments, and customize the listening experience before generating audio.'
        },
        {
          id: 'creators-5',
          question: 'Do I retain the rights to my book?',
          answer: 'Absolutely. You retain full ownership of your intellectual property. NewAudia™ is a distribution platform—we provide the technology to reach listeners, but you maintain complete control over your content. You can deactivate or remove your book at any time.'
        },
        {
          id: 'creators-6',
          question: 'How do I get paid for my work?',
          answer: 'Authors earn based on Reader Credits generated by their books. When readers engage with your content—listening to chapters and asking Audia questions—you earn a share of the credits consumed. Detailed analytics in your Author Console show real-time engagement metrics, and payments are processed according to our revenue sharing terms.'
        },
        {
          id: 'creators-7',
          question: 'What is a Publisher account?',
          answer: 'Publisher accounts are designed for publishing houses managing multiple authors and titles. Publishers can create author profiles, manage a catalog of books, track analytics across their entire portfolio, and handle distribution for all their titles from a single dashboard.'
        },
        {
          id: 'creators-8',
          question: 'Does it cost anything to publish my book?',
          answer: 'Your first manuscript upload is free. Additional manuscript uploads are subject to an upload fee per manuscript. No subscriptions. We operate on a revenue-sharing model—you earn when readers engage with your content. There are no hidden fees, no production costs, and no minimum sales requirements.'
        },
        {
          id: 'creators-9',
          question: 'Do I need to record my own voice or hire a narrator?',
          answer: 'No! That\'s the beauty of NewAudia™. Our AI automatically generates professional-quality narration from your manuscript text. No recording studio, no voice actors, no audio editing required. Your book can go from manuscript to published audiobook in the same day.'
        },
        {
          id: 'creators-10',
          question: 'How long does it take to publish a book?',
          answer: 'Most books can be processed and ready for activation within 30-60 minutes. The time depends on the length of your manuscript and the complexity of your chapter structure. You\'ll receive a notification when your book is ready, and you can activate it immediately or take time to review and customize first.'
        },
        {
          id: 'creators-11',
          question: 'Can I see how readers are engaging with my book?',
          answer: 'Yes! Your Author or Publisher Console includes detailed analytics showing total listeners, listening duration, chapter completion rates, and Audia interaction counts. You can see which parts of your book resonate most with readers and track your earnings in real-time.'
        },
        {
          id: 'creators-12',
          question: 'Can I publish a book that\'s already on Audible or other platforms?',
          answer: 'It depends on your existing agreements. If you have an exclusive distribution deal with another platform, you\'ll need to check those terms. However, if you retain audiobook rights or your existing deal is non-exclusive, you\'re welcome to publish on NewAudia™. Many authors distribute across multiple platforms.'
        },
        {
          id: 'creators-13',
          question: 'Can NewAudia narrate books using my own voice?',
          answer: 'Not yet, but we\'re working on it! We are actively collaborating with Text-to-Speech (TTS) providers to develop the capability for authors to record a short voice sample that can be used to generate rich, realistic AI narration in your actual voice. This feature will allow your audiobook to sound like you—without spending hours in a recording studio. Stay tuned for updates!'
        },
        {
          id: 'creators-14',
          question: 'How do readers discover my book?',
          answer: 'NewAudia™ helps readers find your book through multiple discovery channels: genre and theme browsing, author pages, search functionality, and personalized recommendations. When you complete your book\'s metadata—including a compelling description, accurate genre tags, and professional cover art—you increase your visibility to potential readers.'
        },
        {
          id: 'creators-15',
          question: 'Can I update my book after it\'s published?',
          answer: 'Yes. You can update your book\'s metadata (title, description, cover art, genre) at any time from your console. For content changes to the manuscript itself, you\'ll need to upload a new version and regenerate the audio. Major updates may require temporarily deactivating your book during the process.'
        },
        {
          id: 'creators-16',
          question: 'What content restrictions apply to books on NewAudia™?',
          answer: 'NewAudia™ prohibits content that is illegal, promotes violence or hate, infringes on copyrights, or violates our Terms of Service. All books must be original works you have the rights to publish. We reserve the right to remove content that violates our policies. Review our full Acceptable Use Policy for detailed guidelines.'
        },
        {
          id: 'creators-17',
          question: 'When and how do I get paid?',
          answer: 'Author earnings are calculated based on Reader Credit consumption from your books. Payment schedules and thresholds are detailed in your Author Agreement. You can track your earnings in real-time through your console dashboard. Payments are processed via direct deposit or other standard payment methods based on your region.'
        },
        {
          id: 'creators-18',
          question: 'What if someone copies or pirates my content?',
          answer: 'NewAudia™ takes intellectual property protection seriously. Our platform includes technical measures to prevent unauthorized downloading. If you discover your content being used without permission elsewhere, contact us at info@newaudia.com and we\'ll assist with DMCA takedown procedures and enforcement.'
        }
      ]
    },
    {
      category: 'Using the Platform',
      questions: [
        {
          id: 'platform-1',
          question: 'How do I navigate between chapters while listening?',
          answer: 'The audio player includes chapter navigation controls. You can skip forward/backward between chapters, use the chapter list to jump to any section, or use the progress bar to scrub within a chapter. Your position is automatically saved.'
        },
        {
          id: 'platform-1b',
          question: 'How do I navigate between segments within a chapter?',
          answer: 'Within each chapter, you can navigate between segments using the segment list panel. Click on any segment to jump directly to that point in the audio. The current segment is highlighted, and your position within the segment is tracked automatically.'
        },
        {
          id: 'platform-2',
          question: 'Can I listen offline?',
          answer: 'Currently, NewAudia™ requires an internet connection for streaming audio and AI features. Offline listening is a feature we\'re exploring for future native mobile apps.'
        },
        {
          id: 'platform-2b',
          question: 'Can I adjust the playback speed?',
          answer: 'Variable playback speed is not currently available. NewAudia\'s AI-generated narration is delivered at a standard pace optimized for natural listening and comprehension.'
        },
        {
          id: 'platform-3',
          question: 'Can I skip between chapters?',
          answer: 'Yes! The audio player includes Previous and Next chapter buttons, and you can also select any chapter directly from the chapter list. Your position within each chapter is saved automatically.'
        },
        {
          id: 'platform-4',
          question: 'What does "Activate" mean for a book?',
          answer: 'When authors upload a book, it starts in a draft state visible only to them. "Activating" a book makes it publicly available in the NewAudia™ library for readers to discover and add. Authors can deactivate books at any time to remove them from public view.'
        },
        {
          id: 'platform-5',
          question: 'How do I filter and browse books?',
          answer: 'The Reader Homepage offers multiple ways to discover books: Browse by Author, Browse by Genre, Browse by Theme, or explore Promotional (free) titles. Each section has a dropdown filter to narrow your selection. You can also view book details and read descriptions before adding to your library.'
        }
      ]
    },
    {
      category: 'Account & Billing',
      questions: [
        {
          id: 'accounts-0',
          question: 'Can I sign in with Google?',
          answer: 'Yes! NewAudia™ supports Google Sign-In for a faster, more convenient login experience. Simply click "Continue with Google" on the login or registration page to use your existing Google account. You can also create a traditional account with email and password if you prefer.'
        },
        {
          id: 'accounts-1',
          question: 'What account types are available?',
          answer: 'NewAudia™ offers: Reader accounts for listeners, Author accounts for independent authors publishing their own work, and Publisher accounts for publishing houses managing multiple authors and titles. Each account type has access to different features and dashboards tailored to their needs.'
        },
        {
          id: 'accounts-1b',
          question: 'What is the difference between À La Carte, Reader, and Subscriber memberships?',
          answer: 'À La Carte members purchase credit bundles on-demand without any subscription commitment—ideal for occasional listeners. Reader accounts receive Introduction Credits upon signup and can purchase bundles as needed. Subscribers pay a monthly or annual fee and receive a recurring credit allotment plus discounted bundle pricing. All membership types have full access to the library and Audia.'
        },
        {
          id: 'accounts-2',
          question: 'How do I check my credit balance?',
          answer: 'Your current credit balance is displayed in the navigation bar when logged in. You can also view detailed credit history, usage breakdown, and purchase more credits from the Pricing page or your account settings.'
        },
        {
          id: 'accounts-3',
          question: 'Is this a subscription service?',
          answer: 'No subscription is required to use NewAudia. Free accounts receive starter credits, and you can purchase additional credits à la carte at any time. Optional monthly subscriptions are available for regular readers who want recurring credits and perks. Visit our Pricing page for details.'
        },
        {
          id: 'accounts-4',
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover), PayPal, Apple Pay, Google Pay, and Cash App Pay through our secure payment processor, Stripe. All transactions are encrypted and your payment information is never stored on our servers.'
        },
        {
          id: 'accounts-5',
          question: 'I forgot my password. How do I reset it?',
          answer: 'Click "Forgot Password" on the login page and enter your registered email address. You\'ll receive a password reset link within a few minutes. Check your spam folder if you don\'t see it. The link expires after 24 hours for security. If you still can\'t access your account, contact support at info@newaudia.com.'
        },
        {
          id: 'accounts-6',
          question: 'Can I change my email address or username?',
          answer: 'Yes. You can update your email address and profile information from your account settings. For security, email changes may require verification. If you encounter any issues updating your account information, contact our support team for assistance.'
        },
        {
          id: 'accounts-7',
          question: 'Do Managed Authors follow the same upload policy?',
          answer: 'Managed Authors follow the same manuscript upload policy as Independent Authors. The first manuscript upload is free, and additional uploads are subject to an upload fee per manuscript.'
        }
      ]
    },
    {
      category: 'International & Languages',
      questions: [
        {
          id: 'intl-1',
          question: 'Is NewAudia™ available internationally?',
          answer: 'Yes! NewAudia™ is accessible worldwide. As long as you have an internet connection and a supported browser, you can listen from anywhere. Payment processing is available in most countries, though some features may vary by region.'
        },
        {
          id: 'intl-2',
          question: 'What languages are supported?',
          answer: 'Currently, NewAudia™ primarily supports English-language content and interface. Our AI narration is optimized for English text. We\'re actively working on expanding language support for both the platform interface and book narration. Stay tuned for announcements about additional language support.'
        },
        {
          id: 'intl-3',
          question: 'Can I publish a book in a language other than English?',
          answer: 'At this time, our AI narration engine is optimized for English. Books in other languages may not produce optimal audio quality. We recommend English-language manuscripts for the best listener experience. Multi-language support is on our development roadmap.'
        }
      ]
    },
    {
      category: 'Privacy & Support',
      questions: [
        {
          id: 'privacy-1',
          question: 'What data does NewAudia™ collect?',
          answer: 'We collect only what\'s necessary to provide the service: listening progress (chapter position, duration), the number of Audia interactions (question count only—we do NOT store the content of your questions), and standard account information. We never collect personally identifiable information beyond what you provide at registration, and we never sell any data to third parties.'
        },
        {
          id: 'privacy-2',
          question: 'Can authors see who is listening to their books?',
          answer: 'No. Authors only see aggregated, anonymized statistics—total listeners, engagement rates, and credit earnings. They never see individual reader identities, listening habits, or any personal information. Your conversations with Audia are completely private; we only track the count of interactions for credit purposes, not the content.'
        },
        {
          id: 'privacy-3',
          question: 'How do I contact support?',
          answer: 'For support inquiries, email us at info@newaudia.com. We typically respond within 24-48 hours. For urgent account issues, include your registered email address and a description of the problem. You can also visit our Contact page for additional ways to reach us.'
        },
        {
          id: 'privacy-4',
          question: 'How do I delete my account?',
          answer: 'To delete your account, please contact our support team at info@newaudia.com. We\'ll process your request and remove your personal data in accordance with our Privacy Policy. Note that authors must first deactivate all published books before account deletion.'
        },
        {
          id: 'privacy-5',
          question: 'What is your refund policy?',
          answer: 'Subscription fees may be refundable within the first 7 days if you haven\'t used included credits. Credit bundle purchases are generally non-refundable once credits are added to your account. If you experience technical issues preventing usage, contact support and we\'ll review your case individually. See our Terms of Service for complete refund policy details.'
        },
        {
          id: 'privacy-6',
          question: 'How do I report a problem with a book or content?',
          answer: 'If you encounter technical issues with a specific book (audio problems, missing chapters), or if you believe content violates our policies, please email info@newaudia.com with the book title and a description of the issue. We investigate all reports and take appropriate action.'
        },
        {
          id: 'privacy-7',
          question: 'Is my payment information secure?',
          answer: 'Yes. All payments are processed through secure, PCI-compliant payment processors. NewAudia™ never stores your full credit card number on our servers. All data transmission is encrypted using industry-standard SSL/TLS protocols.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white pb-20 md:pb-0 overflow-x-hidden">
      <Seo {...PAGE_SEO.faq} />
      {isAuthenticated ? (
        <>
          <ReaderNav />
          <ReaderTopMenuBar />
        </>
      ) : (
        <>
          <VisitorNav />
          <VisitorTopMenuBar />
        </>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-emerald-500 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4zIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]"></div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="max-w-4xl mx-auto text-left">
            <p className="text-sm font-semibold text-white/80 mb-3 uppercase tracking-widest">
              FAQ
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl">
              Answers to the most common questions about NewAudia™, our audio-first NFD-SmartBook™ format, and how the platform works for readers and authors during early access.
            </p>
          </div>
        </div>
      </section>

      {/* Prominent Ask Audia Section - AI Help Showcase */}
      <section className="py-8 sm:py-10 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <button
            onClick={() => setShowAskAudiaModal(true)}
            className="w-full group bg-gradient-to-r from-purple-900/60 via-indigo-900/60 to-purple-900/60 hover:from-purple-800/70 hover:via-indigo-800/70 hover:to-purple-800/70 border border-purple-500/40 hover:border-purple-400/60 rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:scale-[1.01] shadow-xl hover:shadow-2xl hover:shadow-purple-500/20"
            data-testid="faq-ask-audia-btn"
          >
            <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
              {/* Avatar with glow effect */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-purple-500/30 rounded-full blur-xl group-hover:bg-purple-400/40 transition-colors"></div>
                <img 
                  src="/audia-avatar.jpg" 
                  alt="Audia - Your AI Assistant" 
                  className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full border-3 border-purple-400/70 group-hover:border-purple-300 transition-colors object-cover shadow-lg"
                />
                <span className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-slate-900 animate-pulse"></span>
              </div>
              
              {/* Text content */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Ask Audia</h3>
                  <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full text-emerald-400 text-xs font-medium">Powered by NewAudia™ AI</span>
                </div>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-xl">
                  Hi, I'm Audia. Please ask me anything you like about NewAudia. I'm always here to help.
                </p>
              </div>
              
              {/* Arrow indicator */}
              <div className="flex-shrink-0 hidden sm:block">
                <div className="w-12 h-12 rounded-full bg-purple-600/30 group-hover:bg-purple-500/50 flex items-center justify-center transition-all group-hover:translate-x-1">
                  <svg className="w-6 h-6 text-purple-300 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-10 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="space-y-4">
            {faqData.map((section, sectionIdx) => (
              <div 
                key={sectionIdx}
                className="bg-slate-800/30 border border-slate-700/50 rounded-2xl overflow-hidden"
              >
                {/* Collapsible Category Header */}
                <button
                  onClick={() => toggleCategory(section.category)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
                  aria-expanded={openCategories[section.category]}
                  data-testid={`faq-category-${section.category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight
                      className={`w-5 h-5 text-purple-400 flex-shrink-0 transition-transform duration-200 ${
                        openCategories[section.category] ? 'rotate-90' : ''
                      }`}
                    />
                    <h2 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide">
                      {section.category}
                    </h2>
                    <span className="text-sm text-slate-400 font-normal normal-case">
                      ({section.questions.length} questions)
                    </span>
                  </div>
                </button>
                
                {/* Questions - Only shown when category is expanded */}
                {openCategories[section.category] && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-700/50">
                    {section.questions.map((item) => (
                      <div
                        key={item.id}
                        className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden backdrop-blur-sm mt-3 first:mt-4"
                      >
                        <button
                          onClick={() => toggleItem(item.id)}
                          className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-800/70 transition-colors"
                          aria-expanded={openItems[item.id]}
                        >
                          <span className="font-semibold text-white text-sm sm:text-base pr-4">
                            {item.question}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                              openItems[item.id] ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openItems[item.id] && (
                          <div className="px-5 pb-4 text-slate-300 text-sm leading-relaxed border-t border-slate-700/50 pt-3">
                            {item.isFounderLink ? (
                              <div>
                                <p className="mb-3">
                                  For more than a decade, audiobooks have lived inside a closed system where most value flows to gatekeepers, not creators or readers. NewAudia was built to challenge that arrangement and prove there's another way.
                                </p>
                                <button
                                  onClick={() => setShowFounderModal(true)}
                                  className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-medium transition-colors"
                                  data-testid="faq-founder-link"
                                >
                                  <Play className="w-4 h-4" />
                                  Hear directly from our founder, R. Dale Brunet
                                </button>
                              </div>
                            ) : (
                              item.answer
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Last Updated & Contact Section */}
          <div className="mt-8 pt-6 border-t border-slate-700/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-500" data-testid="faq-last-updated">
                Last updated: February 2026
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-slate-700/80 border border-slate-700 hover:border-slate-600 rounded-lg text-slate-300 hover:text-white text-sm transition-all"
                data-testid="faq-contact-link"
              >
                <Mail className="w-4 h-4" />
                Still have questions? Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion CTA Section - Only for visitors */}
      {!isAuthenticated && (
        <section className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-y border-purple-500/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Ready to experience NewAudia™?
              </h2>
              <p className="text-base sm:text-lg text-slate-300 mb-8">
                Sign up today and receive <span className="text-purple-400 font-semibold">free Reader Credits</span> to explore our library and meet Audia, your AI reading companion.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setShowRegistrationModal(true)}
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-500 hover:to-indigo-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                  data-testid="faq-get-started-btn"
                >
                  Get Started Free
                </button>
                <Link
                  to="/public/library"
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-slate-200 bg-slate-800/80 border border-slate-700 rounded-lg hover:bg-slate-700 hover:border-slate-600 transition-all"
                >
                  Browse the Library
                </Link>
              </div>
              <p className="mt-6 text-sm text-slate-400">
                No credit card required. Start listening in minutes.
              </p>
            </div>
          </div>
        </section>
      )}

      {isAuthenticated ? (
        <>
          <UniversalFooter />
          <ReaderMobileBottomNav />
        </>
      ) : (
        <>
          <VisitorFooter />
          <VisitorMobileBottomNav />
        </>
      )}

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />

      {/* Ask Audia FAQ Modal */}
      <AskAudiaFAQModal
        isOpen={showAskAudiaModal}
        onClose={() => setShowAskAudiaModal(false)}
      />

      {/* Founder Welcome Modal */}
      <FounderWelcomeModal
        isOpen={showFounderModal}
        onClose={() => setShowFounderModal(false)}
      />
    </div>
  );
};

export default FAQPage;
